<template>
    <div class="ai-agent-training">
        <!-- 主内容区域 -->
        <div class="main-content">
            <!-- 操作按钮组 -->
            <div class="sub-button-group">
                <button class="sub-button orange-button" @click="openAddDialog">
                    <Icon icon="mdi:plus" />
                    新增智能体
                </button>
                <button class="sub-button" @click="refreshList">
                    <Icon icon="mdi:refresh" />
                    刷新列表
                </button>
            </div>

            <!-- 内容区域 -->
            <div class="content-area">
                <!-- 智能体列表 -->
                <div class="agent-list">
                    <!-- 在列表头部添加授权信息显示 -->
                    <div class="list-header">
                        <h3 class="list-title">智能体列表</h3>
                        <div class="list-stats">
                            <!-- 单主体模式提示 -->
                            <div v-if="licenseInfo.singleSubjectMode" class="single-subject-warning">
                                <el-alert :title="licenseInfo.message" type="warning" :closable="false" show-icon />
                            </div>
                            <!-- 正常模式显示 -->
                            <div v-else class="stats-details">
                                <span class="stat-item">总数: {{ licenseInfo.total === -1 ? '无限制' : licenseInfo.total
                                    }}</span>
                                <span class="stat-item">已使用: {{ licenseInfo.used }}</span>
                                <span class="stat-item">剩余: {{ licenseInfo.remaining === -1 ? '无限制' :
                                    licenseInfo.remaining }}</span>

                                <span class="stat-item">启用: {{ enabledCount }}</span>
                                <span class="stat-item">同步次数: {{ totalSyncCount }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- 智能体卡片网格 -->
                    <div class="agent-grid" v-if="agentList.length > 0">
                        <div v-for="agent in agentList" :key="agent.id" class="agent-card"
                            :class="{ disabled: agent.status === 0 }">
                            <div class="card-header">
                                <div class="platform-info">
                                    <div class="platform-icon">
                                        <Icon :icon="getPlatformIcon(agent.platform_type)" width="24" height="24" />
                                    </div>
                                    <div class="platform-details">
                                        <h4 class="site-name">{{ agent.site_name }}</h4>
                                        <p class="site-url">{{ agent.site_url }}</p>
                                    </div>
                                </div>
                                <div class="status-badge">
                                    <el-tag :type="agent.status === 1 ? 'success' : 'info'" size="small">
                                        {{ agent.status === 1 ? '启用' : '禁用' }}
                                    </el-tag>
                                </div>
                            </div>

                            <div class="card-content">
                                <div class="agent-meta">
                                    <!-- 平台信息 -->
                                    <div class="meta-item">
                                        <span class="meta-label">平台:</span>
                                        <span class="meta-value">{{ getPlatformText(agent.platform_type) }}</span>
                                    </div>

                                    <!-- 关联用户信息 -->
                                    <div class="meta-item">
                                        <span class="meta-label">关联用户:</span>
                                        <span class="meta-value user-value">
                                            <div class="user-info">
                                                <div class="user-nickname">{{ getUserInfo(agent.user_id).nickname }}
                                                </div>
                                                <div class="user-phone">{{ getUserInfo(agent.user_id).phone }}</div>
                                            </div>
                                        </span>
                                    </div>

                                    <!-- 认证信息 -->
                                    <div class="meta-item">
                                        <span class="meta-label">认证:</span>
                                        <span class="meta-value">{{ getAuthTypeText(agent.auth_type) }}</span>
                                    </div>

                                    <!-- WordPress用户名显示 -->
                                    <div class="meta-item" v-if="agent.platform_type === 'wordpress' && agent.wp_username">
                                        <span class="meta-label">账号:</span>
                                        <span class="meta-value">{{ agent.wp_username }}</span>
                                    </div>

                                    <div class="meta-item">
                                        <span class="meta-label">同步:</span>
                                        <span class="meta-value">{{ agent.sync_count }} 次</span>
                                    </div>
                                    <div class="meta-item">
                                        <span class="meta-label">最后同步:</span>
                                        <span class="meta-value">{{ formatTime(agent.last_sync_time) }}</span>
                                    </div>
                                </div>
                            </div>

                            <div class="card-actions">
                                <el-button size="small" @click="testConnection(agent)"
                                    :loading="testingAgent === agent.id">
                                    <Icon icon="mdi:connection" />
                                    测试
                                </el-button>
                                <el-button size="small" type="primary" @click="openManageDialog(agent)">
                                    <Icon icon="mdi:cog" />
                                    管理
                                </el-button>
                                <el-button size="small" type="warning" @click="openEditDialog(agent)">
                                    <Icon icon="mdi:pencil" />
                                    编辑
                                </el-button>
                                <el-button size="small" type="danger" @click="handleDelete(agent.id)">
                                    <Icon icon="mdi:delete" />
                                    删除
                                </el-button>
                            </div>
                        </div>
                    </div>
                    <!-- 空状态 -->
                    <div class="empty-state" v-else>
                        <div class="empty-content">
                            <Icon icon="mdi:robot-off" width="60" height="60" color="#909399" />
                            <p class="empty-text">暂无智能体配置</p>
                            <p class="empty-desc">点击上方按钮添加第一个智能体配置</p>
                        </div>
                    </div>

                    <!-- 分页组件 -->
                    <div class="pagination-container" v-if="agentList.length > 0">
                        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                            :total="pagination.total" :page-sizes="[10, 20, 30, 50]"
                            layout="total, sizes, prev, pager, next, jumper" @size-change="fetchAgentList"
                            @current-change="fetchAgentList" />
                    </div>
                </div>
            </div>
        </div>

        <!-- 新增/编辑智能体对话框 -->
        <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px" :close-on-click-modal="false">
            <el-form ref="agentForm" :model="agentForm" :rules="formRules" label-width="120px">
                <el-form-item label="平台类型" prop="platform_type">
                    <el-select v-model="agentForm.platform_type" placeholder="请选择平台" @change="handlePlatformChange">
                        <el-option v-for="platform in platformOptions" :key="platform.value" :label="platform.label"
                            :value="platform.value" />
                    </el-select>
                </el-form-item>

                <el-form-item label="网站名称" prop="site_name">
                    <el-input v-model="agentForm.site_name" placeholder="请输入网站名称" />
                </el-form-item>

                <el-form-item label="网站URL" prop="site_url">
                    <el-input v-model="agentForm.site_url" placeholder="请输入完整的网站URL，如：https://example.com" />
                </el-form-item>

                <!-- WordPress用户名输入框，仅在WordPress平台显示 -->
                <el-form-item label="WordPress账号" prop="wp_username" v-if="agentForm.platform_type === 'wordpress'">
                    <el-input v-model="agentForm.wp_username" placeholder="请输入WordPress用户名" />
                    <div class="form-tip">WordPress后台登录用户名</div>
                </el-form-item>

                <el-form-item label="认证类型" prop="auth_type">
                    <el-select v-model="agentForm.auth_type" placeholder="请选择认证方式">
                        <el-option label="API密钥" value="api_key" />
                        <el-option label="签名认证" value="signature" />
                        <el-option label="Cookie认证" value="cookie" />
                    </el-select>
                </el-form-item>

                <!-- WordPress平台显示为应用密码 -->
                <el-form-item label="API密钥/应用密码" prop="api_key" v-if="agentForm.auth_type !== 'cookie'">
                    <el-input v-model="agentForm.api_key" placeholder="请输入API密钥或应用密码" show-password />
                    <div class="form-tip" v-if="agentForm.platform_type === 'wordpress'">
                        在WordPress后台【用户】→【应用密码】中生成，需去掉空格
                    </div>
                    <div class="form-tip" v-else-if="agentForm.platform_type === 'emlog'">
                        在网站后台API设置中获取
                    </div>
                </el-form-item>

                <el-form-item label="状态">
                    <el-switch v-model="agentForm.status" :active-value="1" :inactive-value="0" active-text="启用"
                        inactive-text="禁用" />
                </el-form-item>

                <!-- 关联用户（仅站点端显示） -->
                <el-form-item label="关联用户" v-if="isEditMode">
                    <div class="form-tip">关联用户ID: {{ agentForm.user_id || '未关联' }}</div>
                </el-form-item>

                <el-form-item label="认证配置" v-if="agentForm.auth_type === 'cookie'">
                    <div class="auth-config-section">
                        <div class="config-item" v-for="(item, index) in authConfigItems" :key="index">
                            <el-input v-model="item.key" placeholder="配置键" style="width: 120px; margin-right: 10px;" />
                            <el-input v-model="item.value" placeholder="配置值" style="flex: 1;" />
                            <el-button type="danger" text @click="removeAuthConfigItem(index)"
                                style="margin-left: 10px;">
                                <Icon icon="mdi:delete" />
                            </el-button>
                        </div>
                        <el-button type="primary" text @click="addAuthConfigItem">
                            <Icon icon="mdi:plus" />
                            添加配置项
                        </el-button>
                    </div>
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitAgentForm" :loading="submitting">
                    确认
                </el-button>
            </template>
        </el-dialog>

        <!-- 智能体管理对话框 -->
        <el-dialog v-model="manageDialogVisible" :title="`管理 - ${currentAgent?.site_name}`" width="900px"
            :close-on-click-modal="false">
            <div class="manage-content" v-if="currentAgent">
                <!-- 标签页 -->
                <el-tabs v-model="activeManageTab" class="manage-tabs">
                    <!-- 分类管理 -->
                    <el-tab-pane label="分类管理" name="categories">
                        <div class="tab-content">
                            <div class="tab-header">
                                <h4>网站分类列表</h4>
                                <el-button size="small" @click="refreshCategories">
                                    <Icon icon="mdi:refresh" />
                                    刷新
                                </el-button>
                            </div>
                            <div class="categories-list">
                                <div v-for="category in categoriesList" :key="category.sid" class="category-item">
                                    <div class="category-info">
                                        <span class="category-name">{{ category.sortname }}</span>
                                        <span class="category-meta">文章数: {{ category.lognum }}</span>
                                    </div>
                                    <div class="category-actions">
                                        <el-tag size="small" v-if="category.alias">别名: {{ category.alias }}</el-tag>
                                    </div>
                                </div>
                                <div class="empty-state" v-if="categoriesList.length === 0">
                                    <p>暂无分类数据</p>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>

                    <!-- 文章管理 -->
                    <el-tab-pane label="文章管理" name="articles">
                        <div class="tab-content">
                            <div class="tab-header">
                                <h4>网站文章列表</h4>
                                <div class="tab-actions">
                                    <el-input v-model="articleSearch.keyword" placeholder="搜索文章标题"
                                        style="width: 200px; margin-right: 10px;" clearable />
                                    <el-button size="small" @click="refreshArticles">
                                        <Icon icon="mdi:refresh" />
                                        刷新
                                    </el-button>
                                </div>
                            </div>
                            <div class="articles-list">
                                <el-table :data="articlesList" style="width: 100%" size="small">
                                    <el-table-column prop="title" label="标题" min-width="200" show-overflow-tooltip />
                                    <el-table-column prop="author_name" label="作者" width="100" />
                                    <el-table-column prop="sort_name" label="分类" width="120" />
                                    <el-table-column prop="views" label="阅读数" width="80" align="center" />
                                    <el-table-column prop="comnum" label="评论数" width="80" align="center" />
                                    <el-table-column prop="date" label="发布时间" width="140" />
                                    <el-table-column label="操作" width="120" fixed="right">
                                        <template #default="{ row }">
                                            <el-button size="small" type="primary" text @click="viewArticleDetail(row)">
                                                详情
                                            </el-button>
                                        </template>
                                    </el-table-column>
                                </el-table>
                                <div class="pagination-container" v-if="articlesPagination.total > 0">
                                    <el-pagination v-model:current-page="articlesPagination.page"
                                        v-model:page-size="articlesPagination.pageSize"
                                        :total="articlesPagination.total" :page-sizes="[10, 20, 30]"
                                        layout="total, sizes, prev, pager, next" @size-change="refreshArticles"
                                        @current-change="refreshArticles" />
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>

                    <!-- 同步文章 -->
                    <el-tab-pane label="同步文章" name="sync">
                        <div class="tab-content">
                            <div class="tab-header">
                                <h4>同步文章到网站</h4>
                                <el-button size="small" type="primary" @click="openSyncDialog">
                                    <Icon icon="mdi:upload" />
                                    同步文章
                                </el-button>
                            </div>
                            <div class="sync-form">
                                <el-form :model="syncForm" label-width="100px">
                                    <el-form-item label="文章标题" prop="title">
                                        <el-input v-model="syncForm.title" placeholder="请输入文章标题" />
                                    </el-form-item>
                                    <el-form-item label="文章内容" prop="content">
                                        <el-input v-model="syncForm.content" type="textarea" :rows="8"
                                            placeholder="请输入文章内容" />
                                    </el-form-item>
                                    <el-form-item label="文章摘要" prop="excerpt">
                                        <el-input v-model="syncForm.excerpt" type="textarea" :rows="3"
                                            placeholder="请输入文章摘要（可选）" />
                                    </el-form-item>
                                    <el-form-item label="文章分类" prop="sort_id">
                                        <el-select v-model="syncForm.sort_id" placeholder="请选择分类" style="width: 100%;">
                                            <el-option v-for="category in categoriesList" :key="category.sid"
                                                :label="category.sortname" :value="category.sid" />
                                        </el-select>
                                    </el-form-item>
                                    <el-form-item label="文章标签" prop="tags">
                                        <el-input v-model="syncForm.tags" placeholder="请输入标签，多个用逗号分隔" />
                                    </el-form-item>
                                    <el-form-item label="发布状态">
                                        <el-radio-group v-model="syncForm.draft">
                                            <el-radio label="n">立即发布</el-radio>
                                            <el-radio label="y">存为草稿</el-radio>
                                        </el-radio-group>
                                    </el-form-item>
                                </el-form>
                            </div>
                            <div class="sync-actions">
                                <el-button @click="testSyncContent">
                                    <Icon icon="mdi:flask" />
                                    测试内容
                                </el-button>
                                <el-button type="primary" @click="submitSyncArticle" :loading="syncing">
                                    <Icon icon="mdi:cloud-sync" />
                                    同步文章
                                </el-button>
                            </div>
                        </div>
                    </el-tab-pane>

                    <!-- 同步日志 -->
                    <el-tab-pane label="同步日志" name="logs">
                        <div class="tab-content">
                            <div class="tab-header">
                                <h4>同步日志记录</h4>
                                <el-button size="small" @click="refreshSyncLogs">
                                    <Icon icon="mdi:refresh" />
                                    刷新
                                </el-button>
                            </div>
                            <div class="logs-list">
                                <el-table :data="syncLogsList" style="width: 100%" size="small">
                                    <el-table-column prop="id" label="ID" width="60" />
                                    <el-table-column label="同步状态" width="100">
                                        <template #default="{ row }">
                                            <el-tag :type="row.sync_status === 'success' ? 'success' : 'danger'"
                                                size="small">
                                                {{ row.sync_status === 'success' ? '成功' : '失败' }}
                                            </el-tag>
                                        </template>
                                    </el-table-column>
                                    <el-table-column prop="article_id" label="文章ID" width="80" />
                                    <el-table-column prop="remote_article_id" label="远程ID" width="80" />
                                    <el-table-column prop="sync_type" label="同步类型" width="100" />
                                    <el-table-column prop="error_message" label="错误信息" min-width="200"
                                        show-overflow-tooltip />
                                    <el-table-column prop="create_time" label="同步时间" width="140" />
                                </el-table>
                                <div class="pagination-container" v-if="syncLogsPagination.total > 0">
                                    <el-pagination v-model:current-page="syncLogsPagination.page"
                                        v-model:page-size="syncLogsPagination.pageSize"
                                        :total="syncLogsPagination.total" :page-sizes="[10, 20, 30]"
                                        layout="total, sizes, prev, pager, next" @size-change="refreshSyncLogs"
                                        @current-change="refreshSyncLogs" />
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </div>

            <template #footer>
                <el-button @click="manageDialogVisible = false">关闭</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { Icon } from '@iconify/vue'
import axios from 'axios'
import config from '@/config.js'
import Cookies from 'js-cookie'

export default {
    name: 'AIAgentTraining',
    components: { Icon },
    data() {
        return {
            loading: false,
            dialogVisible: false,
            manageDialogVisible: false,
            dialogTitle: '新增智能体',
            isEditMode: false,
            submitting: false,
            testingAgent: null,
            syncing: false,
            userList: [], // 用户列表
            userMap: {},  // 用户ID映射
            userLoading: false,
            // 平台选项 - 使用 Iconify 图标
            platformOptions: [
                { value: 'emlog', label: 'Emlog', icon: 'mdi:pencil-ruler' },
                { value: 'wordpress', label: 'WordPress', icon: 'mdi:wordpress' },
                // { value: 'typecho', label: 'Typecho', icon: 'mdi:settings' },
                // { value: 'zblog', label: 'Z-Blog', icon: 'mdi:blog' },
                // { value: 'other', label: '其他CMS', icon: 'mdi:web' }
            ],
            licenseInfo: {
                total: 0,
                used: 0,
                remaining: 0,
                singleSubjectMode: false,
                message: ''
            },
            // 智能体表单 - 添加wp_username字段
            agentForm: {
                id: null,
                platform_type: 'emlog',
                site_name: '',
                site_url: '',
                wp_username: '', // WordPress用户名
                auth_type: 'api_key',
                api_key: '',
                status: 1,
                auth_config: {},
                user_id: null // 关联用户ID
            },

            // 认证配置项
            authConfigItems: [],

            // 表单验证规则 - 添加WordPress用户名验证
            formRules: {
                platform_type: [
                    { required: true, message: '请选择平台类型', trigger: 'change' }
                ],
                site_name: [
                    { required: true, message: '请输入网站名称', trigger: 'blur' }
                ],
                site_url: [
                    { required: true, message: '请输入网站URL', trigger: 'blur' },
                    { type: 'url', message: '请输入有效的URL地址', trigger: 'blur' }
                ],
                wp_username: [
                    {
                        required: () => this.agentForm.platform_type === 'wordpress',
                        message: '请输入WordPress用户名',
                        trigger: 'blur'
                    }
                ],
                auth_type: [
                    { required: true, message: '请选择认证方式', trigger: 'change' }
                ],
                api_key: [
                    {
                        required: () => this.agentForm.auth_type !== 'cookie',
                        message: '请输入API密钥',
                        trigger: 'blur'
                    }
                ]
            },

            // 智能体列表
            agentList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },

            // 当前管理的智能体
            currentAgent: null,
            activeManageTab: 'categories',

            // 分类列表
            categoriesList: [],

            // 文章列表
            articlesList: [],
            articleSearch: {
                keyword: ''
            },
            articlesPagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },

            // 同步表单
            syncForm: {
                title: '',
                content: '',
                excerpt: '',
                sort_id: '',
                tags: '',
                draft: 'n'
            },

            // 同步日志
            syncLogsList: [],
            syncLogsPagination: {
                page: 1,
                pageSize: 10,
                total: 0
            }
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}')
        },
        enabledCount() {
            return this.agentList.filter(agent => agent.status === 1).length
        },
        totalSyncCount() {
            return this.agentList.reduce((total, agent) => total + (agent.sync_count || 0), 0)
        }
    },
    mounted() {
        this.fetchAgentList()
        this.fetchLicenseInfo()
        this.fetchUserList()
    },
    methods: {
        // 获取平台显示文本
        getPlatformText(type) {
            const platform = this.platformOptions.find(p => p.value === type)
            return platform ? platform.label : '未知平台'
        },

        // 获取平台图标
        getPlatformIcon(type) {
            const platform = this.platformOptions.find(p => p.value === type)
            return platform ? platform.icon : 'mdi:help-circle'
        },
        // 获取用户列表
        async fetchUserList() {
            this.userLoading = true
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/site/api/associated_user.php`,
                    {
                        action: 'get_users',
                        store_id: this.userInfo.id
                    }
                )

                if (response.data.data) {
                    this.userList = response.data.data
                    // 构建用户映射
                    this.userMap = {}
                    response.data.data.forEach(user => {
                        this.userMap[user.id] = user
                    })
                }
            } catch (error) {
                console.error('获取用户列表错误:', error)
            } finally {
                this.userLoading = false
            }
        },

        // 根据user_id获取用户信息
        getUserInfo(userId) {
            const user = this.userMap[userId]
            if (user) {
                return {
                    nickname: user.nickname,
                    phone: user.phone,
                    avatar: user.avatar
                }
            }
            return {
                nickname: '管理员',
                phone: '',
                avatar: ''
            }
        },
        // 添加获取授权信息的方法
        async fetchLicenseInfo() {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/site/api/ai_agent_management.php`,
                    {
                        action: 'license_info',
                        store_id: this.userInfo.id
                    }
                )

                if (response.data && !response.data.error) {
                    // 检查是否为单主体模式
                    if (response.data.single_subject_mode) {
                        this.licenseInfo = {
                            total: 1,
                            used: 0,
                            remaining: 1,
                            singleSubjectMode: true,
                            message: response.data.message
                        }
                        // 显示单主体模式提示
                        this.$message.warning(response.data.message)
                    } else {
                        this.licenseInfo = {
                            total: response.data.total || 0,
                            used: response.data.used || 0,
                            remaining: response.data.remaining || 0,
                            singleSubjectMode: false
                        }
                    }
                }
            } catch (error) {
                console.error('获取授权信息失败:', error)
            }
        },

        // 获取认证方式文本
        getAuthTypeText(type) {
            const typeMap = {
                'api_key': 'API密钥',
                'signature': '签名认证',
                'cookie': 'Cookie认证'
            }
            return typeMap[type] || type
        },

        // 格式化时间
        formatTime(timeStr) {
            if (!timeStr) return '从未同步'
            const time = new Date(timeStr)
            const now = new Date()
            const diff = now - time

            if (diff < 60000) return '刚刚'
            if (diff < 3600000) return Math.floor(diff / 60000) + '分钟前'
            if (diff < 86400000) return Math.floor(diff / 3600000) + '小时前'
            if (diff < 2592000000) return Math.floor(diff / 86400000) + '天前'

            return time.getMonth() + 1 + '月' + time.getDate() + '日'
        },

        // 平台变更处理
        handlePlatformChange(value) {
            // 根据平台设置默认值和表单验证
            if (value === 'emlog') {
                this.agentForm.auth_type = 'api_key'
                this.agentForm.wp_username = '' // 清空WordPress用户名
            } else if (value === 'wordpress') {
                this.agentForm.auth_type = 'api_key'
                // WordPress使用应用密码认证，API密钥字段实际存储应用密码
            }
        },

        // 添加认证配置项
        addAuthConfigItem() {
            this.authConfigItems.push({ key: '', value: '' })
        },

        // 删除认证配置项
        removeAuthConfigItem(index) {
            this.authConfigItems.splice(index, 1)
        },

        // 打开新增对话框
        openAddDialog() {
            // 检查授权数量
            if (this.licenseInfo.singleSubjectMode && this.agentList.length >= 1) {
                ElMessage.warning('单主体模式下只能配置一个智能体！')
                return
            }
            if (!this.licenseInfo.singleSubjectMode && this.licenseInfo.total !== -1 && this.licenseInfo.remaining <= 0) {
                ElMessage.warning('授权智能体数已满，请联系管理员充值！')
                return
            }

            this.dialogTitle = '新增智能体'
            this.isEditMode = false
            this.agentForm = {
                id: null,
                platform_type: 'emlog',
                site_name: '',
                site_url: '',
                wp_username: '',
                auth_type: 'api_key',
                api_key: '',
                status: 1,
                auth_config: {},
                user_id: null
            }
            this.authConfigItems = []
            this.dialogVisible = true
        },

        // 在 openEditDialog 方法中添加单主体模式的检查
        openEditDialog(agent) {
            // 在单主体模式下，检查是否已存在其他智能体
            if (this.licenseInfo.singleSubjectMode) {
                const otherAgents = this.agentList.filter(item => item.id !== agent.id);
                if (otherAgents.length > 0) {
                    ElMessage.warning('单主体模式下只能配置一个智能体！');
                    return;
                }
            }

            this.dialogTitle = '编辑智能体'
            this.isEditMode = true
            this.agentForm = {
                id: agent.id,
                platform_type: agent.platform_type,
                site_name: agent.site_name,
                site_url: agent.site_url,
                wp_username: agent.wp_username || '',
                auth_type: agent.auth_type,
                api_key: agent.api_key,
                status: agent.status,
                auth_config: agent.auth_config || {},
                user_id: agent.user_id
            }

            // 处理认证配置项
            this.authConfigItems = []
            if (agent.auth_config && typeof agent.auth_config === 'object') {
                Object.keys(agent.auth_config).forEach(key => {
                    this.authConfigItems.push({
                        key: key,
                        value: agent.auth_config[key]
                    })
                })
            }

            this.dialogVisible = true
        },

        // 修改 submitAgentForm 方法中的错误处理
        submitAgentForm() {
            this.$refs.agentForm.validate(async (valid) => {
                if (valid) {
                    try {
                        this.submitting = true

                        // 处理认证配置
                        if (this.agentForm.auth_type === 'cookie') {
                            const authConfig = {}
                            this.authConfigItems.forEach(item => {
                                if (item.key && item.value) {
                                    authConfig[item.key] = item.value
                                }
                            })
                            this.agentForm.auth_config = authConfig
                        } else {
                            this.agentForm.auth_config = {}
                        }

                        const action = this.isEditMode ? 'update' : 'add'
                        const payload = {
                            ...this.agentForm,
                            store_id: this.userInfo.id
                        }

                        const response = await axios.post(
                            `${config.BASE_URL}/site/api/ai_agent_management.php`,
                            {
                                action,
                                ...payload
                            }
                        )

                        if (response.data.success) {
                            ElMessage.success(this.isEditMode ? '更新成功' : '添加成功')
                            this.dialogVisible = false
                            this.fetchAgentList()
                            this.fetchLicenseInfo() // 刷新授权信息
                        } else {
                            // 改进错误提示
                            const errorMsg = response.data.error || response.data.message || '操作失败'
                            ElMessage.error(errorMsg)
                        }
                    } catch (error) {
                        // 改进错误处理
                        let errorMsg = '操作失败';

                        if (error.response && error.response.data) {
                            const errorData = error.response.data;
                            errorMsg = errorData.error || errorData.message || errorData.reason || '操作失败';

                            // 单主体模式错误处理
                            if (errorData.single_subject_mode) {
                                errorMsg = errorData.message || errorData.error || '单主体模式下操作受限';
                            }
                        } else if (error.message) {
                            errorMsg = error.message;
                        }

                        ElMessage.error(`操作失败: ${errorMsg}`)
                        console.error('表单提交错误:', error)
                    } finally {
                        this.submitting = false
                    }
                }
            })
        },
        // 获取智能体列表
        async fetchAgentList() {
            this.loading = true
            try {
                const { data } = await axios.post(
                    `${config.BASE_URL}/site/api/ai_agent_management.php`,
                    {
                        action: 'list',
                        store_id: this.userInfo.id,
                        page: this.pagination.page,
                        page_size: this.pagination.pageSize
                    }
                )

                if (data.data) {
                    this.agentList = data.data
                    this.pagination.total = data.pagination?.total || data.data.length
                }
            } catch (error) {
                ElMessage.error('获取智能体列表失败')
                console.error('获取智能体列表错误:', error)
            } finally {
                this.loading = false
            }
        },

        // 刷新列表
        refreshList() {
            this.pagination.page = 1
            this.fetchAgentList()
        },

        // 测试连接
        async testConnection(agent) {
            this.testingAgent = agent.id
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/site/api/ai_agent_management.php`,
                    {
                        action: 'test_connection',
                        id: agent.id,
                        store_id: this.userInfo.id
                    }
                )

                if (response.data.success) {
                    ElMessage.success('连接测试成功')
                } else {
                    ElMessage.error(response.data.error || '连接测试失败')
                }
            } catch (error) {
                const errorMsg = error.response?.data?.error || error.message || '连接测试失败'
                ElMessage.error(`连接测试失败: ${errorMsg}`)
            } finally {
                this.testingAgent = null
            }
        },

        // 打开管理对话框
        async openManageDialog(agent) {
            this.currentAgent = agent
            this.manageDialogVisible = true
            this.activeManageTab = 'categories'

            // 加载分类数据
            await this.refreshCategories()
        },

        // 刷新分类列表
        async refreshCategories() {
            if (!this.currentAgent) return

            try {
                const response = await axios.post(
                    `${config.BASE_URL}/site/api/ai_agent_management.php`,
                    {
                        action: 'get_categories',
                        id: this.currentAgent.id,
                        store_id: this.userInfo.id
                    }
                )

                if (response.data.success) {
                    this.categoriesList = response.data.data || []
                } else {
                    ElMessage.error(response.data.error || '获取分类失败')
                }
            } catch (error) {
                const errorMsg = error.response?.data?.error || error.message || '获取分类失败'
                ElMessage.error(`获取分类失败: ${errorMsg}`)
            }
        },

        // 刷新文章列表
        async refreshArticles() {
            if (!this.currentAgent) return

            try {
                const response = await axios.post(
                    `${config.BASE_URL}/site/api/ai_agent_management.php`,
                    {
                        action: 'get_articles',
                        id: this.currentAgent.id,
                        store_id: this.userInfo.id,
                        page: this.articlesPagination.page,
                        count: this.articlesPagination.pageSize
                    }
                )

                if (response.data.success) {
                    this.articlesList = response.data.data?.articles || []
                    this.articlesPagination.total = response.data.data?.total_pages * this.articlesPagination.pageSize || 0
                } else {
                    ElMessage.error(response.data.error || '获取文章失败')
                }
            } catch (error) {
                const errorMsg = error.response?.data?.error || error.message || '获取文章失败'
                ElMessage.error(`获取文章失败: ${errorMsg}`)
            }
        },

        // 查看文章详情
        viewArticleDetail(article) {
            window.open(article.url, '_blank')
        },

        // 打开同步对话框
        openSyncDialog() {
            this.activeManageTab = 'sync'
            this.syncForm = {
                title: '',
                content: '',
                excerpt: '',
                sort_id: this.categoriesList[0]?.sid || '',
                tags: '',
                draft: 'n'
            }
        },

        // 测试同步内容
        testSyncContent() {
            if (!this.syncForm.title || !this.syncForm.content) {
                ElMessage.warning('请填写文章标题和内容')
                return
            }
            ElMessage.success('内容格式检查通过')
        },

        // 提交同步文章
        async submitSyncArticle() {
            if (!this.syncForm.title || !this.syncForm.content) {
                ElMessage.warning('请填写文章标题和内容')
                return
            }

            try {
                this.syncing = true

                const response = await axios.post(
                    `${config.BASE_URL}/site/api/ai_agent_management.php`,
                    {
                        action: 'sync_article',
                        id: this.currentAgent.id,
                        store_id: this.userInfo.id,
                        article_data: this.syncForm
                    }
                )

                if (response.data.success) {
                    ElMessage.success('文章同步成功')
                    // 刷新同步日志
                    this.refreshSyncLogs()
                } else {
                    ElMessage.error(response.data.error || '文章同步失败')
                }
            } catch (error) {
                const errorMsg = error.response?.data?.error || error.message || '文章同步失败'
                ElMessage.error(`文章同步失败: ${errorMsg}`)
            } finally {
                this.syncing = false
            }
        },

        // 刷新同步日志
        async refreshSyncLogs() {
            if (!this.currentAgent) return

            try {
                const response = await axios.post(
                    `${config.BASE_URL}/site/api/ai_agent_management.php`,
                    {
                        action: 'sync_log',
                        store_id: this.userInfo.id,
                        agent_id: this.currentAgent.id,
                        page: this.syncLogsPagination.page,
                        page_size: this.syncLogsPagination.pageSize
                    }
                )

                if (response.data.data) {
                    this.syncLogsList = response.data.data
                    this.syncLogsPagination.total = response.data.pagination?.total || response.data.data.length
                }
            } catch (error) {
                console.error('获取同步日志失败:', error)
            }
        },

        // 删除智能体
        async handleDelete(id) {
            try {
                await ElMessageBox.confirm(
                    '确定要删除该智能体配置吗？',
                    '警告',
                    {
                        confirmButtonText: '确认删除',
                        cancelButtonText: '取消',
                        type: 'warning'
                    }
                )

                const response = await axios.post(
                    `${config.BASE_URL}/site/api/ai_agent_management.php`,
                    {
                        action: 'delete',
                        id: id,
                        store_id: this.userInfo.id
                    }
                )

                if (response.data.success) {
                    ElMessage.success('删除成功')
                    this.fetchAgentList()
                } else {
                    ElMessage.error(response.data.error || '删除失败')
                }
            } catch (error) {
                if (error !== 'cancel') {
                    const errorMsg = error.response?.data?.error || error.message || '删除失败'
                    ElMessage.error(`删除失败: ${errorMsg}`)
                }
            }
        }
    }
}
</script>

<style scoped lang="scss">
.ai-agent-training {
    padding: 20px;
}

.main-content {
    background: hsla(0, 0%, 100%, .1);
    border-radius: 16px;
    padding: 24px;
    backdrop-filter: blur(10px);
}

.sub-button-group {
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    align-items: center;
    flex-wrap: wrap;
}

.sub-button {
    padding: 8px 16px;
    background: hsla(0, 0%, 100%, .1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    color: #e0e6ff;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 6px;

    &:hover {
        background: rgba(255, 112, 72, 0.2);
        border-color: #ff7048;
    }

    &.orange-button {
        background: #ff7048;
        border-color: #ff7048;
        color: #fff;

        &:hover {
            background: #e55a3a;
            border-color: #e55a3a;
        }
    }
}

.content-area {
    min-height: 500px;
}

.agent-list {
    .list-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding-bottom: 16px;
        border-bottom: 1px solid rgba(255, 112, 72, 0.3);
    }

    .list-title {
        color: #e0e6ff;
        font-size: 18px;
        font-weight: 600;
        margin: 0;
    }

    .list-stats {
        display: flex;
        gap: 20px;

        .stat-item {
            color: #ff7048;
            font-size: 14px;
            font-weight: 500;
            margin-right: 10px;
        }
    }
}

.agent-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.agent-card {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 112, 72, 0.2);
    border-radius: 8px;
    padding: 16px;
    transition: all 0.3s ease;

    &:hover {
        border-color: #ff7048;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(255, 112, 72, 0.1);
    }

    &.disabled {
        opacity: 0.6;
        border-color: rgba(255, 255, 255, 0.1);
    }

    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 12px;
    }

    .platform-info {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .platform-icon {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 112, 72, 0.1);
        border-radius: 6px;

        :deep(svg) {
            width: 24px;
            height: 24px;
        }
    }

    .platform-details {
        .site-name {
            color: #e0e6ff;
            font-size: 16px;
            font-weight: 600;
            margin: 0 0 4px 0;
        }

        .site-url {
            color: #a8b1ff;
            font-size: 12px;
            margin: 0;
            word-break: break-all;
        }
    }

    .status-badge {
        flex-shrink: 0;
    }

    .card-content {
        margin-bottom: 16px;
    }

    .agent-meta {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .meta-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .meta-label {
        color: #a8b1ff;
        font-size: 12px;
    }

    .meta-value {
        color: #e0e6ff;
        font-size: 12px;
        font-weight: 500;

        &.user-value {
            text-align: right;
        }
    }

    .card-actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;

        .el-button {
            flex: 1;
            min-width: 50px;
            display: flex;
            align-items: center;
            gap: 4px;
        }
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;

    .empty-content {
        .empty-text {
            color: #e0e6ff;
            font-size: 16px;
            margin: 16px 0 8px 0;
        }

        .empty-desc {
            color: #a8b1ff;
            font-size: 14px;
            margin: 0;
        }
    }
}

.pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 24px;
}

// 表单样式
.form-tip {
    color: #a8b1ff;
    font-size: 12px;
    margin-top: 4px;
}

.auth-config-section {
    .config-item {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
    }
}

// 管理对话框样式
.manage-content {
    .manage-tabs {
        :deep(.el-tabs__header) {
            margin-bottom: 20px;
        }

        :deep(.el-tabs__item) {
            color: #a8b1ff;

            &.is-active {
                color: #ff7048;
            }
        }

        :deep(.el-tabs__active-bar) {
            background-color: #ff7048;
        }
    }

    .tab-content {
        .tab-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 16px;
            padding-bottom: 12px;
            border-bottom: 1px solid rgba(255, 112, 72, 0.2);

            h4 {
                color: #e0e6ff;
                margin: 0;
                font-size: 16px;
            }

            .tab-actions {
                display: flex;
                align-items: center;
                gap: 10px;
            }

            .el-button {
                display: flex;
                align-items: center;
                gap: 4px;
            }
        }
    }

    // 分类列表样式
    .categories-list {
        max-height: 400px;
        overflow-y: auto;

        .category-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 6px;
            margin-bottom: 8px;
            border: 1px solid rgba(255, 255, 255, 0.1);

            &:last-child {
                margin-bottom: 0;
            }

            .category-info {
                .category-name {
                    color: #e0e6ff;
                    font-weight: 500;
                    margin-right: 12px;
                }

                .category-meta {
                    color: #a8b1ff;
                    font-size: 12px;
                }
            }
        }
    }

    // 文章列表样式
    .articles-list {
        :deep(.el-table) {
            background: transparent;

            .el-table__header {
                th {
                    background: rgba(255, 112, 72, 0.1);
                    color: #ff7048;
                    font-weight: 600;
                }
            }

            .el-table__body {
                tr {
                    background: rgba(255, 255, 255, 0.05);

                    &:hover td {
                        background: rgba(255, 112, 72, 0.1);
                    }

                    td {
                        color: #e0e6ff;
                        border-bottom: 1px solid rgba(255, 112, 72, 0.1);
                    }
                }
            }
        }
    }

    // 同步表单样式
    .sync-form {
        margin-bottom: 20px;

        :deep(.el-form-item__label) {
            color: #e0e6ff;
        }

        :deep(.el-input__inner) {
            color: #e0e6ff;

            &:focus {
                border-color: #ff7048;
            }

            &::placeholder {
                color: #a8b1ff;
            }
        }

        :deep(.el-textarea__inner) {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: #e0e6ff;

            &:focus {
                border-color: #ff7048;
            }

            &::placeholder {
                color: #a8b1ff;
            }
        }
    }

    .sync-actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        padding-top: 16px;
        border-top: 1px solid rgba(255, 112, 72, 0.2);

        .el-button {
            display: flex;
            align-items: center;
            gap: 4px;
        }
    }

    // 日志列表样式
    .logs-list {
        :deep(.el-table) {
            background: transparent;

            .el-table__header {
                th {
                    background: rgba(255, 112, 72, 0.1);
                    color: #ff7048;
                    font-weight: 600;
                }
            }

            .el-table__body {
                tr {
                    background: rgba(255, 255, 255, 0.05);

                    &:hover td {
                        background: rgba(255, 112, 72, 0.1);
                    }

                    td {
                        color: #e0e6ff;
                        border-bottom: 1px solid rgba(255, 112, 72, 0.1);
                    }
                }
            }
        }
    }
}

// 对话框样式覆盖
:deep(.el-dialog) {
    background: hsla(0, 0%, 100%, .1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 112, 72, 0.3);

    .el-dialog__header {
        border-bottom: 1px solid rgba(255, 112, 72, 0.2);

        .el-dialog__title {
            color: #e0e6ff;
        }
    }

    .el-dialog__body {
        color: #e0e6ff;
    }

    .el-dialog__footer {
        border-top: 1px solid rgba(255, 112, 72, 0.2);
    }
}

// 表单元素样式覆盖
:deep(.el-form-item__label) {
    color: #e0e6ff;
}

:deep(.el-input__inner) {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #e0e6ff;

    &:focus {
        border-color: #ff7048;
    }

    &::placeholder {
        color: #a8b1ff;
    }
}

:deep(.el-textarea__inner) {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #e0e6ff;

    &:focus {
        border-color: #ff7048;
    }

    &::placeholder {
        color: #a8b1ff;
    }
}

:deep(.el-select .el-input__inner) {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #e0e6ff;
}

:deep(.el-radio) {
    .el-radio__label {
        color: #e0e6ff;
    }
}

:deep(.el-switch__label) {
    color: #e0e6ff;
}

// 分页样式覆盖
:deep(.el-pagination) {

    .btn-prev,
    .btn-next,
    .el-pager li {
        background: rgba(255, 255, 255, 0.05);
        color: #e0e6ff;
        border: 1px solid rgba(255, 255, 255, 0.2);

        &:hover {
            color: #ff7048;
        }

        &.active {
            background: #ff7048;
            border-color: #ff7048;
            color: #fff;
        }
    }

    .el-pagination__total,
    .el-pagination__jump {
        color: #a8b1ff;
    }

    .el-pagination__sizes .el-select .el-input__inner {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: #e0e6ff;
    }
}

// 按钮样式
:deep(.el-button) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

:deep(.el-button.is-text) {
    color: #ff7048;
}

// 响应式设计
@media (max-width: 768px) {
    .ai-agent-training {
        padding: 12px;
    }

    .main-content {
        padding: 16px;
    }

    .agent-grid {
        grid-template-columns: 1fr;
    }

    .list-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .list-stats {
        width: 100%;
        justify-content: space-between;
    }

    .card-actions {
        .el-button {
            min-width: 60px;
            font-size: 12px;
        }
    }

    :deep(.el-dialog) {
        width: 95% !important;
        margin: 20px auto;
    }

    .tab-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
}

@media (max-width: 480px) {
    .sub-button-group {
        flex-direction: column;
        align-items: stretch;

        .sub-button {
            justify-content: center;
        }
    }

    .agent-card {
        padding: 12px;
    }

    .platform-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .card-actions {
        flex-direction: column;

        .el-button {
            width: 100%;
        }
    }
}

.user-info {
    .user-nickname {
        font-weight: 500;
        margin-bottom: 2px;
        line-height: 1.2;
    }

    .user-phone {
        color: #a8b1ff;
        font-size: 11px;
        line-height: 1.2;
    }
}

.single-subject-warning {
    margin-bottom: 10px;
}
</style>